import produce from 'immer';
import _, {zip} from 'underscore';
import {v4 as uuidv4} from 'uuid';

import {assert} from 'Asserts';
import {ClientId} from 'notebook/ELNConstants';
import {
  BaseDedicatedLookupStructuredTableColumn,
  GenericStructuredTableColumn,
  StructuredTableColumn,
  UnknownStructuredTable,
} from 'notebook/structured-tables/StructuredTableConstants';
import {doesNoteIncludeStructuredTableUuid} from 'notebook/structured-tables/util/getAllStructuredTablesInNote';
import excess from 'notebook/tables/excess';
import {TableColumnType} from 'notebook/tables/TableConstants';
import {AnyStructuredTable, Note, NotePartWithItem} from 'notebook/util/NoteUtilConstants';
import APIUtil from 'util/APIUtil';

export class StructuredTablePasteError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'StructuredTablePasteError';
  }
}

function _isDedicatedLookupColumn(
  column: GenericStructuredTableColumn<ClientId>
): column is BaseDedicatedLookupStructuredTableColumn<ClientId> {
  return column.columnType === TableColumnType.DEDICATED_LOOKUP;
}

interface ConvertStructuredTableForPasteContext {
  usedTableNames: Array<string>;
  destinationFileId?: string;
  isDestinationConfig?: boolean;
  note?: Note;
}

/**
 * Convert a structured table to a shape that can be pasted from & to templates. (We enforce whether
 * the copy / paste is across templates in StructuredTablePlugin).
 *
 * This is a helper function for `Editor.convertItemForPaste`.
 *
 * Note: If structured table properties are modified, PasteStructuredTableSentinel-test.ts will throw a type
 * error and indicate to the engineer to handle the modification's impact on copy / paste in this function.
 */
export default function convertStructuredTableForPaste(
  item: NotePartWithItem<AnyStructuredTable>,
  context: ConvertStructuredTableForPasteContext
): {item: NotePartWithItem<AnyStructuredTable>; name: string} {
  const {usedTableNames, destinationFileId, isDestinationConfig, note} = context;
  // If we're cutting and pasting within the same entry, we don't need to do reset all the uuids, because it's
  // only like we moved the table.
  const isCutAndPasteWithinSameDocument =
    destinationFileId &&
    item.item.fileId === destinationFileId &&
    note &&
    // check that we're actually using a note object with noteParts
    // eslint-disable-next-line benchlint/no-in-operator
    'noteParts' in note &&
    !doesNoteIncludeStructuredTableUuid(note, item.item.provenanceUuid);
  const newItem = produce(item, (draftItem) => {
    let name = item.item.name;

    // Table
    // - Generate new table name
    // - Generate new cid for table
    if (Array.from(usedTableNames).includes(name)) {
      name = excess.util.uniqueTableName(
        usedTableNames,
        excess.util.extractTableNamePrefix(draftItem.item.name)
      );
      draftItem.item.name = name;
    }
    draftItem.item.cid = _.uniqueId('st');
    // Convert config tables if destination is an entry or stage entry
    // This can happen on sub-template insert
    if (destinationFileId) {
      draftItem.item.fileId = destinationFileId;
      draftItem.item.isConfig = Boolean(
        APIUtil.isEntryOrSubOrWorkflowTemplate(destinationFileId) ||
        (APIUtil.isDocument(destinationFileId) && isDestinationConfig)
      );
    }

    if (isCutAndPasteWithinSameDocument) {
      assert(
        context.isDestinationConfig == null || context.isDestinationConfig === draftItem.item.isConfig,
        'A table cut and pasted within the same document should have the same config type as the document.'
      );
    }
    // If we're pasting a table and not just cutting and pasting within the same document, we need to
    // generate new uuids for the table, rows, and columns.
    else {
      // Table
      // - Generate new provenance UUID
      // - Reset API ID
      draftItem.item.provenanceUuid = uuidv4();
      draftItem.item.apiId = undefined;

      // Rows
      // - Generate new provenance UUID
      // TODO: BNCH-80679: Handle parameter table copies
      draftItem.item.rows.forEach((row) => {
        row.rowId = uuidv4();
      });

      // Columns
      // - Generate new provenance UUID
      // - Generate new cids
      // - Re-map inputSourceProvenanceUuid for lookup columns
      const newColumns = draftItem.item.columns.map((column) => ({
        ...column,
        cid: _.uniqueId('dtcol'),
        provenanceUuid: uuidv4(),
      }));
      // Re-map inputSourceProvenanceUuid after new columns have been initialized with a new prov. UUID
      for (const [colIdx, column] of draftItem.item.columns.entries()) {
        if (_isDedicatedLookupColumn(column)) {
          const {inputColumnProvenanceUuid} = column;
          const inputColumnIndex = draftItem.item.columns.findIndex(
            (column) => column.provenanceUuid === inputColumnProvenanceUuid
          );
          if (inputColumnIndex === -1) {
            throw new StructuredTablePasteError(
              `Unable to insert table '${item.item.name}': the lookup column '${column.name}' ` +
                `references an input column that no longer exists. ` +
                `Please remove or fix the lookup column in the table and try again.`
            );
          }

          const newLookupColumn = newColumns[colIdx] as BaseDedicatedLookupStructuredTableColumn<ClientId>;
          assert(
            _isDedicatedLookupColumn(newLookupColumn),
            'Columns during table copy & paste were pasted in incorrect order. Found a non-lookup column in pasted table in the same position as a lookup column in the original table.'
          );
          newLookupColumn.inputColumnProvenanceUuid = newColumns[inputColumnIndex].provenanceUuid;
        }
      }
      draftItem.item.columns = newColumns;
    }
  });

  return {item: newItem, name: newItem.item.name};
}

export interface ChangedStructuredTableUuids {
  oldToNewStructuredTableUuids: {[oldUuid: string]: string | undefined};
  oldToNewStructuredTableColumnUuids: {
    [oldTableUuid: string]: {[oldColumnUuid: string]: string | undefined} | undefined;
  };
}

/**
 * Given a list of old Structured Tables and new Structured Tables, create mappings from the old uuids to new
 * uuids. This expects that the tables and their columns are the same shape and order in both lists.
 *
 * This is useful when we still have references to the old uuids following a paste or insert.
 */
export function getChangedStructuredTableUuids(
  oldStructuredTables: Array<UnknownStructuredTable>,
  newStructuredTables: Array<UnknownStructuredTable>
): ChangedStructuredTableUuids {
  const oldToNewStructuredTableUuids: ChangedStructuredTableUuids['oldToNewStructuredTableUuids'] = {};
  const oldToNewStructuredTableColumnUuids: ChangedStructuredTableUuids['oldToNewStructuredTableColumnUuids'] =
    {};
  assert(
    oldStructuredTables.length === newStructuredTables.length,
    'Lists of structured tables should be the same length when trying to check changed UUIDs'
  );
  for (const [oldTable, newTable] of zip(oldStructuredTables, newStructuredTables) as Array<
    [UnknownStructuredTable, UnknownStructuredTable]
  >) {
    const oldTableUuid = oldTable.provenanceUuid;
    const newTableUuid = newTable.provenanceUuid;
    oldToNewStructuredTableUuids[oldTableUuid] = newTableUuid;
    oldToNewStructuredTableColumnUuids[oldTableUuid] = {};
    assert(
      oldTable.columns.length === newTable.columns.length,
      'Structured Tables should have the same number of columns when trying to check changed UUIDs'
    );
    for (const [oldColumn, newColumn] of zip(oldTable.columns, newTable.columns) as Array<
      [StructuredTableColumn, StructuredTableColumn]
    >) {
      const oldColumnUuid = oldColumn.provenanceUuid;
      const newColumnUuid = newColumn.provenanceUuid;
      oldToNewStructuredTableColumnUuids[oldTableUuid][oldColumnUuid] = newColumnUuid;
    }
  }
  return {oldToNewStructuredTableUuids, oldToNewStructuredTableColumnUuids};
}
